import { authReducer } from '@/features/auth'
import { coreReducer } from '@/features/core'
import { personalPlanReducer } from '@/features/personal-plan'
import { planningReducer } from '@/features/planning'
import { resourceGroupsReducer } from '@/features/resource-groups'
import { rolesReducer } from '@/features/roles'
import { routeBrowserReducer } from '@/features/route-browser'
import { shellReducer } from '@/shell'
import {
  Action,
  combineReducers,
  configureStore,
  ThunkAction,
  UnknownAction,
} from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import {
  createMigrate,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist'
import { PersistPartial } from 'redux-persist/es/persistReducer'
import storage from 'redux-persist/lib/storage'
import { migrations } from './migrations'

export const STORE_VERSION = 15

export const reducers = combineReducers({
  core: coreReducer,
  auth: authReducer,
  shell: shellReducer,
  planning: planningReducer,
  routeBrowser: routeBrowserReducer,
  personalPlan: personalPlanReducer,
  resourceGroups: resourceGroupsReducer,
  roles: rolesReducer,
})

const persistConfig = {
  key: import.meta.env.VITE_PLATFORM === 'desktop' ? 'root-desktop' : 'root',
  version: STORE_VERSION,
  storage,
  migrate: createMigrate(migrations, { debug: true }),
}

const resettableReducer = (
  state: ReturnType<typeof reducers> | undefined,
  action: UnknownAction
) => {
  if (action.type === 'store/reset') {
    return reducers(undefined, action)
  }
  return reducers(state, action)
}

const persistedReducer = persistReducer(persistConfig, resettableReducer)
const sentryReduxEnhancer = Sentry.createReduxEnhancer({})
export const createStore = (preloadedState?: RootState & PersistPartial) =>
  configureStore({
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          warnAfter: 100,
        },
      }),

    devTools: true,

    reducer: persistedReducer,
    enhancers: getDefaultEnhancers =>
      getDefaultEnhancers().concat(sentryReduxEnhancer),
    preloadedState,
  })

export const store = createStore()

export type StoreType = typeof store
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof reducers>
export type AppStore = ReturnType<typeof createStore>
export type AppDispatch = typeof store.dispatch
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>

export default store
